'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { sampleOne } from '@/utils';
import useSound from '@/hooks/use-sound';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import Spacer from '@/components/Spacer';
import VisuallyHidden from '@/components/VisuallyHidden';

import LoadingCase from './LoadingCase';
import LoadingSine from './LoadingSine';
import LoadingTouchSlider from './LoadingTouchSlider';
import LoadingBezier from './LoadingBezier';
import LoadingPolar from './LoadingPolar';
import LoadingSlider from './LoadingSlider';
import LoadingLilButtons from './LoadingLilButtons';
import PowerButton from './PowerButton';
import LoadingEarth from './LoadingEarth';

type Props = {};

// Our total size is 145x177, but that includes the case's 3D effect.
// The front panel of the case is 128 x 160, and this is the size I'm going
// to use. I'll let the 3D effect stuff spill out of bounds.
const PANEL_WIDTH = 128;
const PANEL_HEIGHT = 160;

const TinkersynthMachine = ({}: Props) => {
  const [isPoweredOn, setIsPoweredOn] = React.useState(false);
  const [isPressingBtn, setIsPressingBtn] = React.useState(false);

  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const prefersReducedMotion = usePrefersReducedMotion();

  const [play, { stop }] = useSound(
    '/sounds/iidx-button-sprite.mp3',
    {
      sprite: {
        down1: [0, 180],
        up1: [200, 550],
        down2: [800, 180],
        up2: [1000, 180],
        down3: [1200, 180],
        up3: [1400, 180],
        down4: [1600, 180],
        up4: [1800, 180],
      },
    }
  );

  function handlePress() {
    stop();
    const soundId = sampleOne(['down1', 'down2', 'down3', 'down4']);
    play({ id: soundId });
  }

  function handleRelease() {
    stop();
    const soundId = sampleOne(['up1', 'up2', 'up3', 'up4']);
    play({ id: soundId });
  }

  return (
    <Wrapper>
      <Background>
        <LoadingCase />
      </Background>

      <Foreground
        ref={buttonRef}
        style={{ width: PANEL_WIDTH, height: PANEL_HEIGHT }}
        onClick={() => setIsPoweredOn(!isPoweredOn)}
        onPointerDown={() => {
          handlePress();

          window.addEventListener(
            'pointerup',
            (event) => {
              handleRelease();

              if (
                !buttonRef.current?.contains(event.target as Node)
              ) {
                setIsPoweredOn(!isPoweredOn);
              }
            },
            {
              once: true,
            }
          );
        }}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === ' ' || event.key === 'Enter') {
            handlePress();
            setIsPressingBtn(true);
          }
        }}
        onKeyUp={(event: React.KeyboardEvent) => {
          if (event.key === ' ' || event.key === 'Enter') {
            handleRelease();
            setIsPressingBtn(false);
          }
        }}
      >
        <Row aria-hidden="true">
          <LoadingSine
            isPoweredOn={isPoweredOn}
            prefersReducedMotion={prefersReducedMotion}
          />
          <Spacer size={8} />
          <Column>
            <LoadingTouchSlider isPoweredOn={isPoweredOn} />
            <LoadingTouchSlider isPoweredOn={isPoweredOn} />
          </Column>
        </Row>

        <Spacer size={8} />

        <Row aria-hidden="true">
          <LoadingBezier
            isPoweredOn={isPoweredOn}
            prefersReducedMotion={prefersReducedMotion}
          />
          <Spacer size={8} />
          <LoadingPolar
            isPoweredOn={isPoweredOn}
            prefersReducedMotion={prefersReducedMotion}
          />
        </Row>

        <Spacer size={8} />

        <Row aria-hidden="true">
          <Sliders>
            <LoadingSlider
              isPoweredOn={isPoweredOn}
              prefersReducedMotion={prefersReducedMotion}
            />
            <LoadingSlider
              isPoweredOn={isPoweredOn}
              prefersReducedMotion={prefersReducedMotion}
            />
          </Sliders>
          <Spacer size={8} />
          <LoadingEarth isPoweredOn={isPoweredOn} />
          <Spacer size={8} />
          <Column>
            <LoadingLilButtons isPoweredOn={isPoweredOn} />
            <PowerButton isPressingBtn={isPressingBtn} />
          </Column>
        </Row>

        <VisuallyHidden>
          Illustration of a small machine. Triggering this button
          powers on the machine. This is a purely cosmetic effect.
        </VisuallyHidden>
      </Foreground>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const Background = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 0;
  left: 0;

  /* Trim bottom left corner */
  overflow: hidden;
  overflow: clip;
  border-radius: 2px;

  svg {
    max-width: revert;
  }
`;

const Foreground = styled.button`
  position: relative;
  z-index: 2;
  padding: 10px;
  -webkit-tap-highlight-color: transparent;

  @supports selector(:has(*)) {
    &:focus-visible {
      /* Handled in parent */
      outline: none;
    }
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Sliders = styled.div`
  display: flex;
`;

export default TinkersynthMachine;
